import {useEffect, useLayoutEffect, useRef} from 'react'
import useFocusBack from '@/hooks/useFocusBack.mjs'
import useKeystroke from '@/hooks/useKeystroke.mjs'
import useOnResize from '@/hooks/useOnResize.mjs'
import usePointer from '@/hooks/usePointer.mjs'
import {publish} from '@/script/event.mjs'
import {useTreeDocContext} from './TreeDocContext.jsx'
import CanvasSelection from './CanvasSelection.jsx'

export default function Canvas({children, ...props}) {
    const refEl = useRef()
    const doc = useTreeDocContext()
    useFocusBack(refEl)

    useOnResize(
        refEl,
        size => publish(doc, 'canvas_resize', size)
    )

    useLayoutEffect(
        () => {
            doc.canvas = refEl.current
            return () => doc.canvas = null
        },

        []
    )

    // React 的 onWheel 合成事件无法通过
    // event.preventDefault() 阻止浏览器的默认行为
    useEffect(
        () => {
            const el = refEl.current
            const ac = new AbortController()
            const {signal} = ac

            el.addEventListener(
                'wheel',
                e => publish(doc, 'canvas_wheel', e),
                {signal}
            )

            publish(doc, 'load')
            el.focus()
            return () => ac.abort()
        },

        []
    )

    const eventHandlers = {
        onContextMenu: e => {
            e.preventDefault()
            publish(doc, 'canvas_context_menu', e)
        },

        ...useKeystroke({
            onKeyDown: e => {
                publish(doc, 'canvas_key_down', e)
            },

            onKeyUp: e => {
                publish(doc, 'canvas_key_up', e)
            },
        }),

        ...usePointer({
            onClick: e => {
                publish(doc, 'canvas_click', e)
            },

            onClickCapture: e => {
                publish(doc, 'canvas_click_capture', e)
            },

            onDoubleClick: e => {
                publish(doc, 'canvas_double_click', e)
            },

            onDoubleClickCapture: e => {
                publish(doc, 'canvas_double_click_capture', e)
            },

            onPointerDown: e => {
                publish(doc, 'canvas_pointer_down', e)
            },

            onPointerDownCapture: e => {
                publish(doc, 'canvas_pointer_down_capture', e)
            },

            onPointerMove: e => {
                publish(doc, 'canvas_pointer_move', e)
            },

            onPointerMoveCapture: e => {
                publish(doc, 'canvas_pointer_move_capture', e)
            },

            onPointerUp: e => {
                publish(doc, 'canvas_pointer_up', e)
            },

            onPointerUpCapture: e => {
                publish(doc, 'canvas_pointer_up_capture', e)
            },

            onSwipeStart: e => {
                publish(doc, 'canvas_swipe_start', e)
            },

            onSwipeMove: e => {
                publish(doc, 'canvas_swipe_move', e)
            },

            onSwipeEnd: e => {
                publish(doc, 'canvas_swipe_end', e)
            },
        })
    }

    return (
        <div
            ref={refEl}
            role="tree"
            tabIndex="-1"
            {...eventHandlers}
            {...props}
        >
            {children}
            <CanvasSelection />
        </div>
    )
}
